import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import type { AddressDraft } from "@/application/merchants/business_info_edit";
import type { MerchantsRepository } from "@/application/merchants/merchants_repository";
import {
  buildPersonalInfoPatch,
  diffPersonalInfo,
  personalDraftFrom,
  personalEffectContextOf,
  personalEffectsOf,
  personNameOf,
  personRecordOf,
  personRoleOf,
  validatePersonalInfo,
  type PersonalInfoField,
  type PersonRecord,
} from "@/application/merchants/personal_info_edit";
import type { BusinessProfile } from "../types";
import { AddressFields } from "./info-edit/AddressFields";
import { Field } from "./info-edit/Field";
import { ResultStep } from "./info-edit/ResultStep";
import { ReviewChanges } from "./info-edit/ReviewChanges";
import { useInfoSave } from "./info-edit/useInfoSave";

type Step = "edit" | "review" | "result";

type Repo = Pick<
  MerchantsRepository,
  | "getBusinessProfile"
  | "updatePersonalInfo"
  | "writeInfoChangeAudit"
  | "awaitStripePush"
>;

const acceptedCopyFor = (record: PersonRecord | null) => ({
  headline: `Saved. Stripe has the ${personRoleOf(record)}'s new details.`,
  detail: "Business info is unchanged.",
});

const CHECKING_DETAIL =
  "The new details are saved. Stripe is still confirming.";

interface Props {
  business: BusinessProfile;
  /** `saved` is true when at least one save succeeded while the dialog was open. */
  onClose: (saved: boolean) => void;
  repo: Repo;
}

/**
 * Support edits the owner's personal phone and address: Edit → Review →
 * Result. The name is shown, never edited. Mount only while open.
 */
export function EditPersonalInfoDialog({ business, onClose, repo }: Props) {
  const record = personRecordOf(business);
  const [baseline, setBaseline] = useState(() => personalDraftFrom(business));
  const [draft, setDraft] = useState(baseline);
  const [step, setStep] = useState<Step>("edit");
  const [touched, setTouched] = useState<Set<PersonalInfoField>>(new Set());
  const info = useInfoSave(repo, business);

  const changes = diffPersonalInfo(baseline, draft);
  const errors = validatePersonalInfo(baseline, draft);
  const changedFields = new Set(changes.map((c) => c.field));
  const canReview = changes.length > 0 && Object.keys(errors).length === 0;
  const errorFor = (field: PersonalInfoField) =>
    // eslint-disable-next-line security/detect-object-injection -- field is a PersonalInfoField literal
    touched.has(field) ? errors[field] : undefined;
  const touch = (field: PersonalInfoField) =>
    setTouched((prev) => new Set(prev).add(field));
  const setAddress = (patch: Partial<AddressDraft>) =>
    setDraft((d) => ({ ...d, address: { ...d.address, ...patch } }));

  function close() {
    if (info.saving) return;
    onClose(info.saved);
  }

  async function handleSave() {
    const row = await info.save({
      changes,
      readLiveRow: true,
      buildPatch: (base) => buildPersonalInfoPatch(base, draft, changes),
      update: (patch) => repo.updatePersonalInfo(patch),
      audit: {
        action: "merchant.personal_info_changed",
        extra: { person: record },
      },
    });
    if (!row) return;
    const fresh = personalDraftFrom(row);
    setBaseline(fresh);
    setDraft(fresh);
    setTouched(new Set());
    setStep("result");
  }

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-lg">
        {step === "edit" && (
          <>
            <DialogHeader>
              <DialogTitle>Edit personal info</DialogTitle>
              <p className="text-muted-foreground text-sm">
                Merchant: {business.businessName || business.id}
              </p>
              <DialogDescription>
                Changes the {personRoleOf(record)}'s contact details on file and
                in Stripe. Business info is not changed.
              </DialogDescription>
            </DialogHeader>

            <p className="text-sm">
              {record === "representative" ? "Representative" : "Owner"}:{" "}
              {personNameOf(business)}
            </p>

            <div className="space-y-4">
              <Field
                id="personal-phone"
                label="Personal phone"
                error={errorFor("personalPhone")}
              >
                {(describedBy) => (
                  <Input
                    id="personal-phone"
                    type="tel"
                    value={draft.phone}
                    className={
                      changedFields.has("personalPhone")
                        ? "border-blue-500"
                        : undefined
                    }
                    aria-invalid={!!errorFor("personalPhone")}
                    aria-describedby={describedBy}
                    onChange={(e) =>
                      setDraft({ ...draft, phone: e.target.value })
                    }
                    onBlur={() => touch("personalPhone")}
                  />
                )}
              </Field>

              <fieldset
                className="space-y-2"
                aria-describedby={
                  errorFor("personalAddress")
                    ? "personal-address-error"
                    : undefined
                }
                onBlur={() => touch("personalAddress")}
              >
                <legend className="text-foreground mb-1 text-xs font-medium">
                  Personal address
                </legend>
                <AddressFields
                  value={draft.address}
                  changed={changedFields.has("personalAddress")}
                  onChange={setAddress}
                />
                {errorFor("personalAddress") && (
                  <p
                    id="personal-address-error"
                    className="text-xs text-red-600"
                  >
                    {errorFor("personalAddress")}
                  </p>
                )}
              </fieldset>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button disabled={!canReview} onClick={() => setStep("review")}>
                Review changes
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "review" && (
          <>
            <DialogHeader>
              <DialogTitle>Review changes</DialogTitle>
              <DialogDescription>
                {changes.length} field{changes.length === 1 ? "" : "s"} changed.
              </DialogDescription>
            </DialogHeader>

            <ReviewChanges
              changes={changes}
              effects={personalEffectsOf(
                changes,
                personalEffectContextOf(business),
              )}
            />

            {info.saveError && (
              <p role="alert" className="text-sm text-red-600">
                Couldn't save: {info.saveError}. Nothing changed.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                disabled={info.saving}
                onClick={() => {
                  info.clearSaveError();
                  setStep("edit");
                }}
              >
                Back
              </Button>
              <Button disabled={info.saving} onClick={() => void handleSave()}>
                {info.saving && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "result" && (
          <ResultStep
            title="Personal info saved"
            changes={info.savedChanges}
            outcome={info.stripe}
            acceptedCopy={acceptedCopyFor(record)}
            checkingDetail={CHECKING_DETAIL}
            auditOk={info.auditOk}
            retryingAudit={info.retryingAudit}
            onRetryAudit={() => void info.retryAudit()}
            onEditAgain={() => setStep("edit")}
            onDone={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
